/* eslint-disable @typescript-eslint/naming-convention */
// Angular Imports
import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';

// Lodash Imports
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { PolicyChangeRequestType } from '@amfam/shared/models';
// My Account Imports
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { ValidationService } from '@amfam/ui-kit';

import { ChangeRequest, ChangeRequestPolicies } from '../../models';
import { PolicyTransmuteService } from '../../services/policy-transmute.service';

@Component({
  selector: 'ds-change-request-name-form',
  templateUrl: './change-request-name-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChangeRequestNameFormComponent implements OnInit {
  @Input() firstName: string;
  @Input() lastName: string;
  @Output() changeRequestName = new EventEmitter<ChangeRequestPolicies>();
  @Output() cancelEvent = new EventEmitter();
  @Output() valueChangeEvent = new EventEmitter();

  public nameChangeRequestForm: UntypedFormGroup;
  public firstNameInputCtrl: AbstractControl;
  public lastNameInputCtrl: AbstractControl;
  public commentInputCtrl: AbstractControl;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private location: Location,
    public roleService: ImpersonateRolesService,
    private policyTransmuteService: PolicyTransmuteService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit() {
    // Build the form group
    this.nameChangeRequestForm = this.buildNameGroupForm(
      this.formBuilder,
      this.firstName,
      this.lastName
    );

    // Initialize the abstract contols
    // InitializeFormControls function returns an array of absstract controls in the order which we pass the form group items.
    [this.firstNameInputCtrl, this.lastNameInputCtrl, this.commentInputCtrl] =
      this.policyTransmuteService.initializeFormControls(this.nameChangeRequestForm);

    // emits when there is any changes in values by comparing with current value for validating the modal.
    this.nameChangeRequestForm.valueChanges.pipe(take(1)).subscribe(() => {
      this.valueChangeEvent.emit();
    });
  }

  /**
   * @author: Abhishek Singh
   * @param changeRequestForm : Takes a form group and adds the new controls to the form
   * @param firstName: Firstname to initialize the first name control
   * @param lastName: Lastname to initialize the last name control
   * @returns FormGroup : Return the form which has the new controls and the initialized values.
   */
  public buildNameGroupForm(
    fb: UntypedFormBuilder,
    firstName: string,
    lastName: string
  ): UntypedFormGroup {
    return fb.group({
      firstNameInputCtrl: [
        firstName,
        Validators.compose([
          Validators.required,
          ValidationService.firstNameEntryValidator,
          ValidationService.specialCharValidator,
          Validators.maxLength(30)
        ])
      ],
      lastNameInputCtrl: [
        lastName,
        Validators.compose([
          Validators.required,
          ValidationService.lastNameEntryValidator,
          ValidationService.firstNameEntryValidator,
          ValidationService.specialCharValidator,
          ValidationService.nolastNameValidatior,
          Validators.maxLength(40)
        ])
      ],
      commentInputCtrl: [
        '',
        Validators.compose([Validators.maxLength(850), ValidationService.textInputValidator])
      ]
    });
  }

  /**
   * @author: Abhishek Singh
   * @param void
   * @returns void
   * @description On click of the submit button grab the required feilds from the change request form
   * build it match the changeRequest type and emit it out to the parent to do stuff with it. We don't
   * care what the parent does with it, its their problem.
   */
  public submit(): void {
    let changeRequest = new ChangeRequest();

    changeRequest = Object.assign({}, changeRequest, {
      policyChangeType: PolicyChangeRequestType.NAME_CHANGE
    });
    changeRequest = Object.assign({}, changeRequest, {
      firstName: this.nameChangeRequestForm.controls.firstNameInputCtrl.value
    });
    changeRequest = Object.assign({}, changeRequest, {
      lastName: this.nameChangeRequestForm.controls.lastNameInputCtrl.value
    });
    changeRequest = Object.assign({}, changeRequest, { comment: this.commentInputCtrl.value });

    this.analyticsFacade.trackEvent({
      event: 'change_profile_success',
      profile_change_type: 'name'
    });

    // Emit the selected policies
    this.changeRequestName.emit(changeRequest);
  }

  /**
   * @author: Abhishek Singh
   * @returns void
   * @description Self explanatory
   */
  public cancel(): void {
    this.cancelEvent.emit();
  }
}
